import { Component, TemplateRef, effect, model, viewChild } from '@angular/core';
import { Observable, of } from 'rxjs';

import { DocumentPreviewComponent } from '../../reports/document-preview/document-preview.component';
import { ReportToolbarComponent } from '../../reports/report-toolbar/report-toolbar.component';
import { SummaryTopComponent } from '../../summary-top/summary-top.component';

/**
 * Bulk print: the combined PDF of SEVERAL records of one document type, one
 * document per page, shown in the same preview + toolbar the single-document
 * screens use — so Print, Download and Email behave identically, they just act on
 * the batch (4Spaces item 12).
 *
 * Generic on purpose: the caller passes a PipelineDocumentTypes key and the ids in
 * the order they should print, so any browse can open it with no new code here.
 *
 * Printing is not a write action, so this is available to read-only roles — there is
 * no Save on the header.
 */
@Component({
  selector: 'app-bulk-document-modal',
  templateUrl: './bulk-document-modal.component.html',
  styleUrl: './bulk-document-modal.component.scss',
  imports: [SummaryTopComponent, ReportToolbarComponent, DocumentPreviewComponent],
})
export class BulkDocumentModalComponent {
  /** A PipelineDocumentTypes key. */
  public readonly documentType = model<string>();
  /** The records to print, one page each, IN THIS ORDER — the caller sorts. */
  public readonly reportIds = model<string[]>([]);
  /** Shown in the modal header, the report selector and the download file name. */
  public readonly header = model($localize`Bulk Print`);
  /** Off for a batch that must not go to one address - several customers' invoices (D-8). */
  public readonly showEmail = model(true);
  /**
   * Records the batch when the user prints or downloads it (status PRINTED / DOWNLOADED).
   * Unset = nothing is recorded, which is what Despatch Notes bulk print does.
   */
  public readonly batchAudit = model<(status: string) => Observable<unknown>>();

  private readonly reportToolbar = viewChild(ReportToolbarComponent);
  private readonly preview = viewChild<TemplateRef<any>>('preview');

  // app-summary-top renders nothing without a model, and its buttons are gated on an
  // id that is not Guid.Empty. Nothing reads the value — there is no single record
  // behind a batch — so a stable marker id opens the gate. A FIELD, never a getter: a
  // getter hands the async pipe a new observable every change-detection pass.
  protected readonly model$ = of({ id: '11111111-1111-1111-1111-111111111111' });

  constructor() {
    // ng-bootstrap renders the modal synchronously inside modalService.open(), BEFORE
    // the opener can set documentType/reportIds — so wiring the toolbar up in ngOnInit
    // or ngAfterViewInit would register a report with no ids and preview nothing. An
    // effect re-runs when the ids (and the view children) arrive, which is the earliest
    // point at which the batch is actually known.
    effect(() => {
      const documentType = this.documentType();
      const reportIds = this.reportIds();
      const toolbar = this.reportToolbar();
      const preview = this.preview();
      if (!documentType || !reportIds?.length || !toolbar || !preview) {
        return;
      }

      // One entry: the selector shows the batch's name, and the toolbar's Email/
      // Download/Print act on reportIds through the bulk pipeline endpoints.
      toolbar.setReports(
        [
          {
            report: this.header(),
            template: preview,
            documentType,
            reportIds,
            hideEmail: !this.showEmail(),
            batchAudit: this.batchAudit(),
          },
        ],
        true,
      );
    });
  }
}
